import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { Mag } from '../components/mag.js';
import { MagChatBubble } from '../components/mag-chat/mag-chat-bubble.js';
import { MagChatComposer } from '../components/mag-chat/mag-chat-composer.js';
import { MagChatOpener } from '../components/mag-chat/mag-chat-opener.js';
import { MagThinkingBubble } from '../components/mag-chat/mag-chat-thinking.js';
import { magToast } from '../components/toaster.js';
import { TopNav } from '../components/top-nav.js';
import {
  type BrandBrainEnvelope,
  type CampaignDetail,
  type CampaignKind,
  type CampaignPlan,
  cancelCampaignDraft,
  draftCampaign,
  fetchCampaign,
  type fetchMe,
  type IntakeMessage,
  isCampaignPlan,
  postIntakeTurn,
  postIntakeTurnStreaming,
} from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { brandBrainQuery, brandsQuery, meQuery } from '../lib/queries.js';
import { useScrollToBottom } from '../lib/use-scroll-to-bottom.js';

// BRIEF screen — new-campaign handoff §4.2, refactored for the multi-turn
// intake pipeline (s120). Mag and the founder go back and forth until
// Mag classifies the campaign kind, then the screen hands off to the
// drafting page where the matching downstream agent runs.
//
// On entry: if we have a `campaignId` search param, we resume an existing
// intake chat. Otherwise the first submission creates the campaign,
// seeds the founder's opener, and runs Mag's first reply synchronously
// — by the time the chat first paints, both bubbles are already there.

// [a11y] The opener describes the composer, so focusing the composer on
// landing announces mag's question instead of dropping a screen-reader user
// into a bare textarea. Only wired while the opener is on screen — once the
// conversation starts, the opener unmounts and a dangling aria-describedby
// points at nothing.
const BRIEF_OPENER_ID = 'brief-opener';

const briefSearchSchema = z.object({
  source: z.enum(['analytics']).optional(),
  campaignId: z.string().uuid().optional(),
});
type BriefSearch = z.infer<typeof briefSearchSchema>;

/** Where the chat navigates when status flips to 'ready'. organic_meta
 *  has its own slot-based detail surface (no unified review screen),
 *  so we route there directly. Every other pipeline kind lands on
 *  the standard /review screen, which now renders the planning_log
 *  trail on top for panel-drafted plans. */
function readyDestination(
  kind: CampaignKind,
): '/brand/$slug/campaigns/new/review/$id' | '/brand/$slug/campaigns/$id' {
  if (kind === 'organic_meta') return '/brand/$slug/campaigns/$id';
  return '/brand/$slug/campaigns/new/review/$id';
}

export const Route = createFileRoute('/brand/$slug/campaigns/new/brief')({
  validateSearch: briefSearchSchema,
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    const envelope = await queryClient.ensureQueryData(brandBrainQuery(brand.id));
    return { me, envelope, brand };
  },
  loader: ({ context }) =>
    context as {
      me: Awaited<ReturnType<typeof fetchMe>>;
      envelope: BrandBrainEnvelope | null;
      brand: { id: string; name: string; slug: string };
    },
  component: BriefPage,
});

function BriefPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { brand } = Route.useLoaderData();
  const search = Route.useSearch() as BriefSearch;
  const fromAnalytics = search.source === 'analytics';
  useBrandTitle(brand.slug, [t`new campaign`]);

  // Local chat state. `campaignId` is null until the first turn lands;
  // `messages` mirrors whatever the server has confirmed. While Mag is
  // working, `pendingFounder` holds the founder's optimistic bubble so
  // the UI doesn't appear frozen.
  const [campaignId, setCampaignId] = useState<string | null>(search.campaignId ?? null);
  const [messages, setMessages] = useState<IntakeMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [pendingFounder, setPendingFounder] = useState<string | null>(null);
  const [magThinking, setMagThinking] = useState(false);
  // Hydrating means we're loading an existing campaign's intake_messages
  // off the server. While true, render placeholder dots in the chat
  // viewport so the founder doesn't see an empty composer flash.
  const [hydrating, setHydrating] = useState<boolean>(search.campaignId !== undefined);
  // True when the campaign has already left the 'intake' state — we
  // show the conversation read-only, hide the composer, and surface a
  // small status note ("captured — building next").
  const [readOnly, setReadOnly] = useState(false);
  // How many of the initial messages came from the server (used to
  // stagger their slide-in animation; new bubbles added during the
  // session animate independently with no delay).
  const initialMessageCount = useRef(0);
  const transitionGuard = useRef(false);
  // When Mag classifies, we surface a manual handoff card. Where the
  // founder lands depends on kind: creative_ads goes to the drafting
  // screen; non-creative kinds (no real pipeline today) go back to the
  // campaigns list with a "captured" status. The handoff card adapts
  // its copy + button target to match.
  const [transitionTo, setTransitionTo] = useState<{
    campaignId: string;
    target: 'drafting' | 'captured';
  } | null>(null);
  // Plan-mode (default for creative_ads). When the intake agent
  // classifies as creative_ads, the api kicks off the multi-agent
  // panel in the background and streams "tool call" lines into
  // intake_messages. This chat keeps polling for those lines instead
  // of bouncing to /drafting. When the panel finishes (status flips
  // to 'ready'), we navigate to /review.
  const [panelRunning, setPanelRunning] = useState(false);
  // [spec 1bf1c9b8] The drafter's plan, shown while the critics and the
  // synthesizer are still working. Held separately from the final plan
  // because it is deliberately not the same thing — the panel overwrites it.
  const [provisionalPlan, setProvisionalPlan] = useState<CampaignPlan | null>(null);
  // [s379] Mag's reply as it streams in. Grows through the turn, then
  // clears the moment the persisted thread carries it — or the moment
  // the turn fails.
  const [liveText, setLiveText] = useState('');

  // Hydrate existing intake messages when the route is reached with a
  // campaignId in the URL. Without this, resuming an in-progress chat
  // would render a fresh opener instead of the real conversation.
  useEffect(() => {
    if (!search.campaignId) return;
    let cancelled = false;
    (async () => {
      try {
        const campaign = await fetchCampaign(search.campaignId as string);
        if (cancelled || !campaign) return;
        // Captured = a classified campaign whose pipeline isn't built
        // yet. Today that's only conversion_path. (analytics_setup now
        // has a real pipeline and routes through drafting/review like
        // creative_ads.)
        const isCaptured =
          campaign.status === 'ready' && !campaign.draft && campaign.kind === 'conversion_path';
        if (campaign.status !== 'intake' && !isCaptured) {
          // Any pipeline kind in 'drafting' = work running in the
          // background. Stay in the chat, hydrate intake + any
          // panel-progress turns, and start polling. We navigate
          // when the draft is ready.
          const hasPipeline =
            campaign.kind === 'creative_ads' ||
            campaign.kind === 'analytics_setup' ||
            campaign.kind === 'organic_meta';
          if (campaign.status === 'drafting' && hasPipeline) {
            initialMessageCount.current = campaign.intakeMessages.length;
            setMessages(campaign.intakeMessages);
            transitionGuard.current = true;
            setPanelRunning(true);
            return;
          }
          // Already ready (panel finished while away) — bounce to the
          // kind-appropriate destination.
          if (campaign.status === 'ready') {
            navigate({
              to: readyDestination(campaign.kind),
              params: { slug: brand.slug, id: campaign.id },
            });
          } else if (campaign.status === 'drafting') {
            // Captured / no-pipeline kind that somehow ended up in
            // 'drafting' — fall through to the legacy drafting
            // surface rather than spinning forever.
            navigate({
              to: '/brand/$slug/campaigns/new/drafting/$id',
              params: { slug: brand.slug, id: campaign.id },
            });
          }
          return;
        }
        initialMessageCount.current = campaign.intakeMessages.length;
        setMessages(campaign.intakeMessages);
        if (isCaptured) setReadOnly(true);
      } catch (err) {
        magToast.error("mag couldn't load that chat.", founderErrorCopy(err));
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [search.campaignId, navigate, brand.slug]);

  const handleHandoff = () => {
    if (!transitionTo) return;
    if (transitionTo.target === 'drafting') {
      navigate({
        to: '/brand/$slug/campaigns/new/drafting/$id',
        params: { slug: brand.slug, id: transitionTo.campaignId },
      });
    } else {
      navigate({
        to: '/brand/$slug/campaigns',
        params: { slug: brand.slug },
      });
    }
  };

  // s204 — chat-app autoscroll. Messages live in their own
  // overflow-y-auto container (messagesScrollRef); we scroll THAT
  // container to keep the last bubble in view rather than the
  // window. This is the ChatGPT pattern: the composer stays pinned
  // to the bottom of the chat column while the message history
  // scrolls independently above it.
  //
  // [2026-10-03] This used to fire on (messages.length, pendingFounder,
  // magThinking) only — all DISCRETE events — so nothing re-scrolled
  // while mag's reply STREAMED. Her bubble grew token by token under a
  // viewport that stayed put, and a long answer finished with its last
  // third below the fold. liveText is the missing trigger.
  //
  // It also always asked for behavior:'smooth', which is the wrong
  // choice mid-stream: the animation takes ~300-500ms, the next chunk
  // lands in ~30-80ms, and each call restarts the animation from
  // further behind so it never catches the growing bubble (s162.26).
  // useScrollToBottom's `streaming` flag already encodes that fix —
  // instant per frame, which READS as smooth because there is no
  // animation to interrupt — so use the shared hook instead of a
  // fourth private copy of this effect.
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const [bottomAnchorRef, scrollToBottom] = useScrollToBottom();
  // Don't yank a founder who scrolled up to re-read something. Sticky
  // while they're within a bubble's height of the bottom, which is
  // where they are unless they deliberately left.
  const stickToBottom = useRef(true);
  const handleMessagesScroll = () => {
    const el = messagesScrollRef.current;
    if (!el) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: messages.length is the intended trigger, not the array identity
  useEffect(() => {
    if (!stickToBottom.current) return;
    scrollToBottom(liveText.length > 0);
  }, [messages.length, pendingFounder, magThinking, liveText, scrollToBottom]);

  // Background-work poll: while a pipeline worker (panel for
  // creative_ads, legacy workers for analytics_setup / organic_meta)
  // runs in the background, poll the campaign every ~1.5s, pull in
  // fresh intake_messages (which carry any panel-progress lines), and
  // bounce to the kind-appropriate destination on 'ready'.
  useEffect(() => {
    if (!panelRunning || !campaignId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      if (cancelled) return;
      try {
        const updated = await fetchCampaign(campaignId);
        if (cancelled || !updated) return;
        setMessages(updated.intakeMessages);
        // [spec 1bf1c9b8] draft present + still 'drafting' = the drafter has
        // landed but the panel is not done. That column PAIR is the whole
        // signal; there is no provisional status value to check.
        if (updated.status !== 'ready' && updated.draft && isCampaignPlan(updated.draft)) {
          setProvisionalPlan(updated.draft);
        }
        if (updated.status === 'ready') {
          setPanelRunning(false);
          navigate({
            to: readyDestination(updated.kind),
            params: { slug: brand.slug, id: updated.id },
          });
          return;
        }
        // [spec eebec8e1] Back in 'intake' means the draft was cancelled —
        // by this tab or another one. Drop the provisional plan with it, or
        // the founder returns to the chat with the refused draft still
        // sitting under it.
        if (updated.status === 'intake') {
          setPanelRunning(false);
          setProvisionalPlan(null);
          return;
        }
        if (updated.status === 'failed') {
          setPanelRunning(false);
          magToast.error(
            t`mag hit an error drafting this.`,
            updated.failureReason ?? t`try again from the campaigns list.`,
          );
          return;
        }
      } catch (err) {
        // Swallow transient network errors — the next tick will
        // retry. Only surface if the founder needs to act.
        console.warn('[brief] poll tick failed', err);
      }
      timer = setTimeout(tick, 1500);
    };
    timer = setTimeout(tick, 800);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [panelRunning, campaignId, navigate, brand.slug, t]);

  const intakeClosed = transitionGuard.current;

  // [spec eebec8e1] Cancel the running panel and come back to the chat. The
  // thread is already on screen and stays there; the server moves the row to
  // 'intake' and returns it, so the composer reopens with the whole
  // conversation and the founder's next message is their correction.
  const [cancellingDraft, setCancellingDraft] = useState(false);
  const handleCancelDraft = async () => {
    if (!campaignId || cancellingDraft) return;
    setCancellingDraft(true);
    try {
      const updated = await cancelCampaignDraft(campaignId);
      setMessages(updated.intakeMessages);
      setProvisionalPlan(null);
      setPanelRunning(false);
    } catch (err) {
      // A 409 here is the honest race: the panel finished between the render
      // and the click. Say that rather than reporting a failure — the poll
      // is about to navigate them to a finished plan.
      magToast.error(
        t`couldn't cancel that draft.`,
        err instanceof Error ? err.message : t`try again in a moment.`,
      );
    } finally {
      setCancellingDraft(false);
    }
  };

  const handleSend = async () => {
    if (intakeClosed) return;
    const trimmed = draft.trim();
    if (!trimmed) {
      magToast.error(t`mag needs a sentence to go on.`, t`drop one line and i'll do the rest.`);
      return;
    }
    setDraft('');
    // Their own turn always pulls them back to the bottom, whatever
    // they were re-reading a moment ago.
    stickToBottom.current = true;
    setPendingFounder(trimmed);
    setMagThinking(true);
    setLiveText('');
    try {
      let updated: CampaignDetail;
      if (campaignId) {
        // [s379] Stream the turn so mag's words appear as she writes
        // them instead of ~6s of dots. There are no tool calls to show
        // on this surface — the intake agent is a single LLM call — so
        // text_chunk is the only event that carries anything.
        //
        // Zero deltas is a supported outcome (a provider that can't
        // stream falls back server-side), and the dots bubble is what
        // the founder sees then: exactly today's behavior.
        updated = await postIntakeTurnStreaming(campaignId, trimmed, (event) => {
          if (event.kind === 'text_chunk') {
            setLiveText((prev) => prev + event.text);
          }
        });
      } else {
        // First turn creates the campaign; no stream endpoint for it.
        updated = await draftCampaign(trimmed, brand.id);
        setCampaignId(updated.id);
      }
      setMessages(updated.intakeMessages);
      setPendingFounder(null);
      setMagThinking(false);
      // The persisted thread now carries this reply — drop the live
      // copy so the bubble isn't rendered twice for one turn.
      setLiveText('');
      // A 200 WITH NO REPLY IS A FAILED TURN. createCampaign catches an
      // agent error, returns the row carrying only the founder's message,
      // and its comment says "the dashboard will surface a retry" — this is
      // that retry, which had never been written. On 2026-10-03 a ClickHouse
      // timeout killed the first intake turn and the founder was left with
      // their own message, no answer, and no error: nothing threw, so the
      // catch below never ran.
      if (updated.intakeMessages.at(-1)?.role !== 'mag') {
        setDraft(trimmed);
        magToast.error(
          t`mag didn't get back to you.`,
          t`that turn didn't go through — your message is back in the box, send it again.`,
        );
        return;
      }
      // Anything past 'intake' means Mag classified. For kinds with a
      // real downstream pipeline (creative_ads, analytics_setup,
      // organic_meta) we hand off to drafting. Anything else is
      // captured for now — the API already set status='ready' with
      // draft=null and the campaigns list will show it as
      // "captured · building next".
      // organic_meta added in s233.8 — pipeline shipped in s216-p3
      // (content plan) → s216-p5 (autopublish); the brief route was
      // never updated, so organic briefs were silently falling into
      // the captured dead-end.
      if (updated.status !== 'intake' && !transitionGuard.current) {
        transitionGuard.current = true;
        // Any pipeline kind runs inside the chat — the worker fires
        // in the background, the chat polls for progress + the final
        // draft. Captured (no-pipeline) kinds still get the legacy
        // handoff so the founder sees "saved this, no pipeline yet".
        const hasPipeline =
          updated.kind === 'creative_ads' ||
          updated.kind === 'analytics_setup' ||
          updated.kind === 'organic_meta';
        if (hasPipeline && updated.status === 'drafting') {
          setPanelRunning(true);
        } else {
          setTransitionTo({ campaignId: updated.id, target: 'captured' });
        }
      }
    } catch (err) {
      setPendingFounder(null);
      setMagThinking(false);
      // [s379] Clear the live bubble. A failed turn that leaves half a
      // sentence from mag stranded above an error message reads as if
      // she said something she didn't (spec 868e6180, FM-4). And hand
      // the founder their own words back rather than making them retype
      // what they already sent.
      setLiveText('');
      setDraft(trimmed);
      magToast.error("mag couldn't continue.", founderErrorCopy(err, 'try again.'));
    }
  };

  const hasConversation = messages.length > 0 || pendingFounder !== null;

  // For initial hydrated messages, stagger the slide-in so the
  // conversation reads as appearing in sequence (40ms per bubble).
  // Anything beyond `initialMessageCount.current` came in during this
  // session and animates with no delay — it's a "fresh" turn.
  const staggerDelayMs = (index: number): number => {
    if (index >= initialMessageCount.current) return 0;
    return Math.min(index * 50, 240);
  };

  // s204 — chat-app layout. Full-viewport main, TopNav + sub-header
  // (back link + step pill) at the top, then the chat column has
  // its OWN scroll with a composer pinned to the bottom.
  //
  // [2026-10-03] h-screen → --app-h. 100vh assumes Safari's bottom
  // toolbar is retracted, so on a phone the pinned composer sat ~90px
  // under it. See lib/use-app-height.ts for why neither vh nor dvh
  // alone is enough.
  return (
    <main className="flex h-[var(--app-h,100dvh)] flex-col overflow-hidden bg-[var(--cream)]">
      <TopNav active="campaigns" />
      {/* [2026-10-03] Three mono uppercase labels do not fit across a
          390pt phone, and the row is a fixed h-12 — so "back to
          campaigns" wrapped to a second line INSIDE a 48px box and got
          clipped. Below sm the back link shortens to "← back" and the
          "new campaign" eyebrow drops entirely: the step pill beside it
          already says where you are. */}
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-[var(--line)] bg-[var(--cream)] px-4 sm:px-7">
        <Link
          to="/brand/$slug/campaigns"
          params={{ slug: brand.slug }}
          className="shrink-0 whitespace-nowrap font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ink)]/55 hover:text-[var(--ink)]"
        >
          {'←'}{' '}
          <span className="sm:hidden">
            <Trans>back</Trans>
          </span>
          <span className="hidden sm:inline">
            <Trans>back to campaigns</Trans>
          </span>
        </Link>
        <div className="flex shrink-0 items-center gap-3">
          <span className="hidden font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)] sm:inline">
            {'↘'} <Trans>NEW CAMPAIGN</Trans>
          </span>
          <span className="inline-flex items-center whitespace-nowrap rounded-full bg-[var(--spell)] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink)]">
            <Trans>STEP 1 OF 2 · BRIEF</Trans>
          </span>
        </div>
      </header>

      <section className="mx-auto flex w-full max-w-[1280px] flex-1 overflow-hidden px-4 sm:px-7">
        {/* [2026-10-03] One centered column, always. The tips card used
            to live in a right-hand aside on the first turn, which put
            the thing you read BEFORE writing a brief off to the side of
            the thing you write it in. It now sits directly under mag's
            opener, in reading order. */}
        <div className="mx-auto grid w-full max-w-[940px] grid-cols-1 gap-8">
          {/* ─── chat column — scrollable messages + pinned composer */}
          {/* px-1 + py on the chat column gives the composer's
              shadow offset room to breathe without getting clipped
              by the parent section's overflow-hidden. */}
          <div className="flex min-w-0 flex-col overflow-hidden px-1 pt-6 pb-5">
            <div
              ref={messagesScrollRef}
              role="log"
              aria-live="polite"
              aria-label={t`conversation with mag`}
              onScroll={handleMessagesScroll}
              className="mag-scrollbar-subtle flex-1 overflow-y-auto pr-2"
            >
              <div className="flex flex-col gap-6 pb-2">
                {hydrating ? (
                  <HydratingPlaceholder />
                ) : !hasConversation ? (
                  <MagChatOpener
                    id={BRIEF_OPENER_ID}
                    headline={
                      fromAnalytics
                        ? '“people are already on the site. who do you want them to be?”'
                        : '“ok. what are we trying to do?”'
                    }
                    body={
                      fromAnalytics
                        ? "tell me what you're selling, who it's for, and what counts as a win. i'll figure out whether this is an ads play, a funnel pass, or just better tracking — and run the right one."
                        : "one paragraph. tell me the goal, who it's for, and what counts as a win. i'll figure out whether this is creative work, funnel work, or analytics setup before we get to numbers."
                    }
                  />
                ) : null}
                {!hasConversation && !readOnly ? <BriefTipsPanel firstTurn /> : null}
                {/* [2026-06-08] Render via groupedConversation so
                    consecutive panel-progress turns collapse into one
                    PanelProgressCard instead of stacking as N mono
                    CLI lines. The card shows mag-voice stages with
                    pulse/check indicators — feels like progress, not
                    a log dump. */}
                {groupConversationForRender(messages).map((entry, gi) =>
                  entry.kind === 'message' ? (
                    <MagChatBubble
                      key={`${entry.message.role}-${entry.message.createdAt}`}
                      speaker={entry.message.role}
                      body={entry.message.body}
                      animationDelayMs={staggerDelayMs(gi)}
                    />
                  ) : (
                    <PanelProgressCard
                      key={`panel-${entry.startedAt}`}
                      steps={entry.steps}
                      runningStill={panelRunning}
                      onCancel={handleCancelDraft}
                      cancelling={cancellingDraft}
                    />
                  ),
                )}
                {/* [spec 1bf1c9b8] The drafter's plan, readable ~90s before
                    the panel finishes. Sits below the progress card so the
                    checklist still reads as the thing that is running. */}
                {provisionalPlan && panelRunning ? (
                  <ProvisionalPlanCard plan={provisionalPlan} />
                ) : null}
                {pendingFounder ? (
                  <MagChatBubble speaker="founder" body={pendingFounder} pending />
                ) : null}
                {magThinking ? <MagThinkingBubble liveText={liveText} /> : null}
                {transitionTo ? (
                  <HandoffCard target={transitionTo.target} onGo={handleHandoff} />
                ) : null}
                {readOnly ? <CapturedNote slug={brand.slug} /> : null}
                <div ref={bottomAnchorRef} aria-hidden="true" />
              </div>
            </div>

            {transitionTo || readOnly || panelRunning ? null : (
              // pt-8 preserves the spacing the old local Composer carried
              // internally (mt-8) now that the shared composer owns no
              // outer margin.
              <div className="shrink-0 pt-8 pb-[env(safe-area-inset-bottom)]">
                <MagChatComposer
                  // [2026-10-03] autoFocus only on the FIRST turn, where
                  // mag's opener is on screen to describe what focus
                  // skipped — that pairing is the rule, and the two were
                  // split: autoFocus unconditional, describedById only
                  // when the opener exists. It is also a mobile bug.
                  // Returning to a brief already in progress popped the
                  // keyboard on landing, covering the thread the founder
                  // came back to read.
                  autoFocus={!hasConversation}
                  {...(hasConversation ? {} : { describedById: BRIEF_OPENER_ID })}
                  draft={draft}
                  onDraftChange={setDraft}
                  onSend={handleSend}
                  disabled={intakeClosed || magThinking}
                  placeholder={
                    hasConversation
                      ? t`reply to mag…`
                      : t`tell mag what you want to do — a paragraph is enough.`
                  }
                  chips={
                    hasConversation ? null : (
                      <ShortcutChips
                        disabled={intakeClosed || magThinking}
                        onChip={(text) =>
                          setDraft(draft.trim() ? `${draft.trimEnd()}\n${text}` : text)
                        }
                      />
                    )
                  }
                />
              </div>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

/** Inline handoff card shown after Mag classifies. Two variants:
 *  - 'drafting': creative-ads kind, Mag is about to draft a real
 *    campaign → button routes to /drafting/:id.
 *  - 'captured': non-creative kind, no pipeline yet → button returns
 *    to /campaigns. Honest copy: "saved this, no pipeline yet".
 *  No auto-redirect either way — the transition is the founder's call. */
function HandoffCard({ target, onGo }: { target: 'drafting' | 'captured'; onGo: () => void }) {
  const { t } = useLingui();
  if (target === 'drafting') {
    return (
      <div className="bubble-in flex items-end gap-3">
        <Mag size={36} mood="casting" />
        <div className="flex-1">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>mag · ready to draft</Trans>
          </p>
          <div className="mt-1.5 flex items-center gap-3 rounded-[16px_16px_16px_4px] border-[1.5px] border-dashed border-[var(--ink)]/40 bg-[var(--paper)] px-4 py-3">
            <p className="flex-1 text-[14px] leading-[1.4] text-[var(--ink)]/80">
              <Trans>i've got what i need. ready when you are.</Trans>
            </p>
            <button type="button" onClick={onGo} className="btn" aria-label={t`start drafting`}>
              <Trans>let's go</Trans>
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="bubble-in flex items-end gap-3">
      <Mag size={36} mood="casting" />
      <div className="flex-1">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>mag · captured · building soon</Trans>
        </p>
        <div className="mt-1.5 rounded-[16px_16px_16px_4px] border-[1.5px] border-dashed border-[var(--ink)]/40 bg-[var(--paper)] p-4">
          <p className="text-[14px] leading-[1.45] text-[var(--ink)]/80">
            <Trans>
              this campaign isn't a creative-ads play, so i don't have the pipeline built for it
              yet. i've saved the brief — it'll show up on the campaigns list as{' '}
              <strong>captured</strong>, and i'll come back to it once the right agent ships.
            </Trans>
          </p>
          <div className="mt-3 flex justify-end">
            <button type="button" onClick={onGo} className="btn" aria-label={t`back to campaigns`}>
              <Trans>back to campaigns</Trans>
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Static note rendered below the chat when a captured (non-creative)
 *  campaign is being re-read. The conversation stays visible above so
 *  the founder can remember what they briefed; this footer explains why
 *  the composer is gone and where the campaign is in the lifecycle. */
function CapturedNote({ slug }: { slug: string }) {
  return (
    <div className="bubble-in flex items-end gap-3">
      <Mag size={36} mood="napping" />
      <div className="flex-1">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>captured · building next</Trans>
        </p>
        <div className="mt-1.5 rounded-[16px_16px_16px_4px] border border-dashed border-[var(--ink)]/30 bg-[var(--paper)] p-4">
          <p className="text-[14px] leading-[1.45] text-[var(--ink)]/80">
            <Trans>
              this brief is saved. the pipeline for this kind of campaign isn't built yet — i'll
              re-open the chat when it is.
            </Trans>
          </p>
          <div className="mt-3 flex justify-end">
            <Link
              to="/brand/$slug/campaigns"
              params={{ slug }}
              className="btn btn-ghost text-[11px]"
            >
              <Trans>back to campaigns</Trans>
              <span className="btn-arrow">→</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Placeholder shown while we fetch an existing intake. A single
 *  thinking-dots bubble avoids the layout flash from empty viewport →
 *  hydrated conversation. Matches the same drawer-style mag bubble
 *  shape so the loading state doesn't feel like a separate widget. */
function HydratingPlaceholder() {
  return (
    <div className="flex items-end gap-2.5 opacity-70">
      <Mag size={28} mood="napping" />
      <div className="inline-flex items-center gap-1.5 rounded-[16px_16px_16px_4px] border-[1.5px] border-[var(--ink)] bg-[var(--ink)] px-4 py-3">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="block h-1.5 w-1.5 rounded-full bg-[var(--cream)]"
            style={{
              animation: 'magThinkingDot 1.1s ease-in-out infinite',
              animationDelay: `${i * 150}ms`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

/** Footer pill shown while a background draft worker is running. The
 *  composer is hidden during this phase — the chat navigates to the
 *  kind-appropriate destination automatically when status flips to
 *  'ready'. Copy stays generic (no "panel" reference) because this
 *  same pill covers organic_meta + analytics_setup, not just the
 *  creative_ads panel. */
// [2026-06-08] Conversation grouping — collapses consecutive
// panel-progress turns into a single PanelProgressCard.
//
// The panel writes ~10 progress turns ("→ sketching the plan", "✓
// first pass · 3 deliverables drafted", etc.) over ~30-60 seconds.
// Rendering them as N individual mono bubbles in the chat reads
// like a CLI log dump — fine for engineers, hostile for founders.
// Grouping them into one styled card with a stages checklist gives
// the same information at a glance + animates state changes (pulse
// → check) instead of just streaming text.
type ConversationEntry =
  | { kind: 'message'; message: IntakeMessage }
  | { kind: 'panel-group'; steps: PanelProgressStep[]; startedAt: string };

interface PanelProgressStep {
  /** Parsed status from the body's leading symbol. */
  status: 'pending' | 'active' | 'done' | 'failed';
  /** The text after the symbol (e.g. "sketching the plan"). */
  label: string;
}

function groupConversationForRender(messages: IntakeMessage[]): ConversationEntry[] {
  const entries: ConversationEntry[] = [];
  let pendingGroup: { steps: PanelProgressStep[]; startedAt: string } | null = null;
  for (const m of messages) {
    if (m.role === 'panel-progress') {
      const step = parsePanelStep(m.body);
      if (!pendingGroup) {
        pendingGroup = { steps: [], startedAt: m.createdAt };
      }
      // Replace the last step if the new line is the DONE version of
      // the previously-active line (active → done collapse). Matched
      // by label equality of the slug after the symbol.
      const last = pendingGroup.steps[pendingGroup.steps.length - 1];
      if (last && last.status === 'active' && stepsMatch(last, step)) {
        pendingGroup.steps[pendingGroup.steps.length - 1] = step;
      } else {
        pendingGroup.steps.push(step);
      }
      continue;
    }
    if (pendingGroup) {
      entries.push({ kind: 'panel-group', ...pendingGroup });
      pendingGroup = null;
    }
    entries.push({ kind: 'message', message: m });
  }
  if (pendingGroup) {
    entries.push({ kind: 'panel-group', ...pendingGroup });
  }
  return entries;
}

function parsePanelStep(body: string): PanelProgressStep {
  // Server contract — first char identifies the state:
  //   "→ <label>"     → active
  //   "✓ <label>"     → done
  //   "✗ <label>"     → failed (crash)
  //   "panel: <...>"  → setup line; we coerce to active w/ trimmed
  //                     label so it shows above the first step
  const trimmed = body.trim();
  if (trimmed.startsWith('→')) {
    return { status: 'active', label: trimmed.slice(1).trim() };
  }
  if (trimmed.startsWith('✓')) {
    return { status: 'done', label: trimmed.slice(1).trim() };
  }
  if (trimmed.startsWith('✗')) {
    return { status: 'failed', label: trimmed.slice(1).trim() };
  }
  if (trimmed.startsWith('panel:')) {
    return { status: 'active', label: trimmed.slice('panel:'.length).trim() };
  }
  return { status: 'active', label: trimmed };
}

/** Two steps are "the same step at different stages" when their
 *  labels match modulo the trailing receipts. The server emits:
 *    "→ checking the targeting"
 *    "✓ targeting checked"
 *  — different surface forms for the same beat. We match by the
 *  noun-phrase the label centers on (after a leading verb / before
 *  a trailing past-tense). Crude but good enough for the v1 hooks. */
function stepsMatch(a: PanelProgressStep, b: PanelProgressStep): boolean {
  const noun = (s: string): string => {
    // Strip leading verbs the server uses ("sketching", "checking",
    // "tightening", "lining up") + trailing past-tense markers
    // ("checked", "ready", "drafted").
    return s
      .toLowerCase()
      .replace(/^(sketching the|checking the|tightening the|lining up a)\s+/, '')
      .replace(/\s+(checked|ready|drafted|done)$/, '')
      .trim();
  };
  return noun(a.label) === noun(b.label);
}

/** [spec 1bf1c9b8] The drafter's plan, while the critics and the synthesizer
 *  are still running.
 *
 *  The panel takes ~140s and used to write nothing until the very end, so the
 *  founder watched a checklist and learned what Mag was proposing only once
 *  everything was final. The drafter's plan exists ~90s before that.
 *
 *  IT SAYS IT IS PROVISIONAL, and that is not a disclaimer — the synthesizer
 *  WILL edit this plan, and a founder who read it as final would experience
 *  the correction as the product changing its mind behind their back. Naming
 *  it up front turns the same edit into progress they were told to expect.
 *
 *  Read-only by construction: there is nothing to click. Approval lives on
 *  /review, which is unreachable until status flips to 'ready'. */
function ProvisionalPlanCard({ plan }: { plan: CampaignPlan }) {
  return (
    <div className="bubble-in flex items-end gap-2.5">
      <Mag size={28} mood="default" />
      <div className="flex-1 rounded-[16px_16px_16px_4px] border-[1.5px] border-dashed border-[var(--ink)]/25 bg-white/70 px-4 pt-3 pb-3">
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            <Trans>↘ mag · first pass</Trans>
          </p>
          <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--plum)]" />
        </div>
        <p className="mt-1 font-display text-[14.5px] font-semibold leading-snug text-[var(--ink)]">
          {plan.name}
        </p>
        {/* MEASURED, not guessed: a real pitch here is ~734 characters of
            unbroken prose — three sentences doing three jobs (what she is
            building, why the split, what each part tests) with zero newlines
            in the data, so there is nothing for a paragraph rule to act on.
            Layout cannot break a monolith, but it can stop making it worse:
            cap the measure so lines do not run the full bubble, and give the
            text room to breathe at this size. The content shape is the
            agent's to fix. */}
        <p className="mt-1.5 max-w-[62ch] text-[13.5px] leading-[1.6] text-[var(--ink)]/80">
          {plan.pitch}
        </p>
        {plan.deliverables.length > 0 ? (
          // The per-deliverable "why" lines are a DIFFERENT register from the
          // pitch — each is a claim about one thing she will ship. Rendered as
          // a run of same-coloured text under a dense paragraph they read as
          // more pitch. A rule and a real bullet separate the two without
          // adding chrome.
          <ul className="mt-3 flex flex-col gap-1.5 border-[var(--ink)]/10 border-t pt-2.5">
            {plan.deliverables.map((d) => (
              <li
                key={d.id}
                className="flex max-w-[62ch] gap-2 text-[12.5px] leading-[1.5] text-[var(--ink)]/65"
              >
                <span aria-hidden className="text-[var(--plum)]">
                  ·
                </span>
                <span>{d.why}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/45">
          <Trans>still reviewing this with myself — it may change before it's final.</Trans>
        </p>
      </div>
    </div>
  );
}

/** [2026-06-08] Grouped panel progress card — replaces the old
 *  mono-line dump (drafter / critics / synthesizer · $0.0243). Reads
 *  as a checklist mag is working through: pulse on the active step,
 *  check on the done steps, hollow circle on pending. Closes with
 *  "draft ready" when the synth finishes. */
function PanelProgressCard({
  steps,
  runningStill,
  onCancel,
  cancelling,
}: {
  steps: PanelProgressStep[];
  runningStill: boolean;
  onCancel: () => void;
  cancelling: boolean;
}) {
  // Last step's status drives the header copy: active = working, done
  // = ready, failed = crashed. If the panel is still in flight per
  // the parent's runningStill flag but no NEW step has landed yet,
  // we still read "working" so the founder knows things haven't
  // silently stalled.
  const last = steps[steps.length - 1];
  const anyFailed = steps.some((s) => s.status === 'failed');
  const allDone = steps.every((s) => s.status === 'done');
  const headline = anyFailed
    ? 'something broke.'
    : allDone && !runningStill
      ? 'draft ready.'
      : 'working on your draft.';
  const eyebrow = anyFailed
    ? '↘ mag · couldn’t ship'
    : allDone && !runningStill
      ? '↘ mag · done'
      : '↘ mag · drafting';
  return (
    <div className="bubble-in flex items-end gap-2.5">
      <Mag size={28} mood="default" />
      <div className="flex-1 rounded-[16px_16px_16px_4px] border-[1.5px] border-[var(--ink)]/15 bg-white px-4 pt-3 pb-3">
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {eyebrow}
          </p>
          {!anyFailed && !allDone ? (
            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--plum)]" />
          ) : null}
        </div>
        <p className="mt-1 font-display text-[14.5px] font-semibold leading-snug text-[var(--ink)]">
          {headline}
        </p>
        <ul className="mt-3 flex flex-col gap-1.5">
          {steps.map((s, idx) => (
            <PanelStepRow
              // biome-ignore lint/suspicious/noArrayIndexKey: step labels can repeat across the panel sequence; index disambiguates the otherwise-colliding key
              key={`${idx}-${s.label}`}
              status={
                // The visually-active step is the most-recent one when
                // the panel is still running AND that step is itself
                // active. Earlier active steps that got superseded by
                // a 'done' (via stepsMatch collapse) already became
                // done in the grouping pass.
                s.status === 'active' && last === s && runningStill ? 'active' : s.status
              }
              label={s.label}
            />
          ))}
        </ul>
        {allDone && !runningStill ? (
          <p className="mt-3 text-[11.5px] leading-snug text-[var(--ink)]/55">
            <Trans>review next — you'll see the plan once it loads.</Trans>
          </p>
        ) : null}
        {/* [spec eebec8e1 C-4] Only while the panel is actually running, and
            the copy does not overclaim: the stage in flight still finishes
            and still bills, so this says stop, never "stopped". Right-aligned
            per the project's action-button rule. */}
        {runningStill && !anyFailed ? (
          <div className="mt-3 flex items-center justify-end gap-3 border-t border-[var(--ink)]/10 pt-2.5">
            <p className="text-[11px] leading-snug text-[var(--ink)]/45">
              <Trans>the step that's running will finish first.</Trans>
            </p>
            <button
              type="button"
              onClick={onCancel}
              disabled={cancelling}
              className="shrink-0 rounded-full border-[1.5px] border-[var(--ink)]/20 px-3 py-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/65 transition hover:border-[var(--ink)]/40 hover:text-[var(--ink)] disabled:opacity-50"
            >
              {cancelling ? <Trans>stopping…</Trans> : <Trans>stop drafting</Trans>}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function PanelStepRow({ status, label }: { status: PanelProgressStep['status']; label: string }) {
  return (
    <li className="flex items-center gap-2.5 text-[13px] leading-snug">
      <PanelStepIndicator status={status} />
      <span
        className={
          status === 'pending'
            ? 'text-[var(--ink)]/40'
            : status === 'failed'
              ? 'text-[var(--pulse)]'
              : status === 'active'
                ? 'text-[var(--ink)]'
                : 'text-[var(--ink)]/75'
        }
      >
        {label}
      </span>
    </li>
  );
}

function PanelStepIndicator({ status }: { status: PanelProgressStep['status'] }) {
  // Unified with the onboarding competition step indicators (see
  // brand.$slug.onboarding.competition.tsx). Same affordance, same
  // motion, same color semantics — sage for done, plum-pulse for
  // active. Status-keyed remount triggers mag-check-pop fresh on the
  // active → done transition.
  return (
    <span
      key={status}
      aria-hidden="true"
      className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-[background-color,border-color] duration-300 ease-out ${
        status === 'done'
          ? 'mag-check-pop bg-[var(--sage)]'
          : status === 'active'
            ? 'mag-pulse-soft bg-[var(--plum)]'
            : status === 'failed'
              ? 'bg-[var(--pulse)] text-[8px] font-bold text-[var(--cream)]'
              : 'border border-[var(--ink)]/20 bg-transparent'
      }`}
    >
      {status === 'done' ? (
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <polyline points="4 8 7 11 12 6" />
        </svg>
      ) : status === 'failed' ? (
        '×'
      ) : null}
    </span>
  );
}

function ShortcutChips({
  onChip,
  disabled,
}: {
  onChip: (text: string) => void;
  disabled: boolean;
}) {
  const { t } = useLingui();
  // Built at render, never at module level: a module-level t`` evaluates
  // before any locale is active and bakes English in for the process.
  // `id` is the React key — a translated label would move with the
  // language and remount every chip on a locale switch.
  const chips: { id: string; label: string; text: string }[] = [
    {
      id: 'competitor',
      label: t`point at a competitor`,
      text: t`we want to position against [competitor.com] — they get attention but our angle is different because…`,
    },
    {
      id: 'tweet',
      label: t`paste a tweet`,
      text: t`i saw this tweet and want to test the angle: `,
    },
    {
      id: 'past-brief',
      label: t`copy past brief`,
      text: t`do something like our last one but for a different audience — `,
    },
  ];
  return (
    <div className="mt-3 flex flex-wrap justify-end gap-2">
      {chips.map((c) => (
        <button
          key={c.id}
          type="button"
          disabled={disabled}
          onClick={() => onChip(c.text)}
          className="group inline-flex items-center gap-1 rounded-full border-[1.5px] border-[var(--ink)]/15 bg-[var(--paper)] px-3 py-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/65 transition-colors hover:border-[var(--ink)]/40 hover:bg-[var(--spell-soft)] hover:text-[var(--ink)] disabled:opacity-50"
        >
          <span aria-hidden className="mag-arrow mag-arrow-ne text-[var(--plum)]">
            ↗
          </span>
          {c.label}
        </button>
      ))}
    </div>
  );
}

/** s203 — "briefs that land" companion card. First-turn only; it
 *  disappears the moment the conversation starts so it never competes
 *  with mag's actual replies.
 *
 *  [2026-10-03] Moved out of the right-hand aside and into the thread,
 *  directly under mag's opener. ml-[38px] is not a magic number: it is
 *  the avatar (28) + the row gap (2.5 → 10px), so the card's left edge
 *  lands on the bubble's text edge and the two read as one column. */
function BriefTipsPanel({ firstTurn }: { firstTurn: boolean }) {
  const { t } = useLingui();
  // Read after the hook, never before it: an early return above a hook
  // call is a conditional hook.
  if (!firstTurn) return null;
  const tips = [
    {
      id: 'win',
      label: t`what counts as a win`,
      body: t`name the metric (signups, paid trials, hit $5k MRR). otherwise mag picks her own.`,
    },
    {
      id: 'audience',
      label: t`who you want to reach`,
      body: t`one specific kind of person beats a long demographic.`,
    },
    {
      id: 'angle',
      label: t`what makes this not generic`,
      body: t`angle, timing, a thing your competitors don't do — mag uses this to write copy that doesn't sound like everyone else.`,
    },
  ];
  return (
    <article className="bubble-in ml-[38px] max-w-[85%] rounded-2xl border-[1.5px] border-[var(--line-strong)] bg-[var(--paper)] p-5">
      <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
        {'↘'} <Trans>briefs that land</Trans>
      </p>
      <ul className="mt-4 flex flex-col gap-4">
        {tips.map((tip) => (
          <li key={tip.id}>
            <p className="font-display text-[14px] font-bold leading-tight tracking-[-0.01em] text-[var(--ink)]">
              {tip.label}
            </p>
            <p className="mt-1 font-serif text-[13.5px] leading-[1.45] text-[var(--ink)]/70">
              {tip.body}
            </p>
          </li>
        ))}
      </ul>
    </article>
  );
}
